import React from 'react';
import {
  Button,
  Gutter,
  Divider,
  CardSection,
  Well,
  CondensedHeader,
  CondensedLarge,
  Flashy,
  PluralizeUtil,
  DateUtil,
  Banner,
  Paragraph,
  ParagraphHeader,
  ReadonlyTextarea,
  Tooltip,
  Layout,
  LayoutItem,
  IconButton,
  BusinessTitlesUtil,
  TaxonomyUtil,
} from '@axiom/ui';
import type { FullCandidateAccount, Taxonomy } from '@axiom/validation';

import { TalentUtil } from '../../utils/talent-util';
import { IndustriesUtil } from '../../utils/industries-util';
import { CandidateUtil } from '../../utils/candidate-util';

import { MyBenchCardAvailability } from './MyBenchCardAvailability';
import { MyBenchCardExpectedChange } from './MyBenchCardExpectedChange';

const { getDisplayableIndustries } = IndustriesUtil;

export type EditModalModeType = 're-engage' | 'manage' | null;
export type MyBenchCardSectionsProps = {
  candidate: FullCandidateAccount;
  practiceAreaText: string;
  isEngaged?: boolean;
  isEnvoyUser?: boolean;
  onClickShowTalent: () => void;
  onClickEditEngage: (type: EditModalModeType) => void;
  taxonomy: Taxonomy;
};

export const MyBenchCardSection = ({
  candidate,
  practiceAreaText,
  isEngaged,
  isEnvoyUser,
  onClickShowTalent,
  onClickEditEngage,
  taxonomy,
}: MyBenchCardSectionsProps) => {
  const displayableIndustries = getDisplayableIndustries(candidate.industries);
  const uniqueLawDegreeSchools = CandidateUtil.getUniqueLawDegreeSchools(
    candidate.lawDegreeSchools
  );

  const formattedLegalRole = BusinessTitlesUtil.locationConversion({
    businessTitle:
      TaxonomyUtil.getBusinessTitle(taxonomy, candidate.businessTitle)
        ?.displayName ?? null,
    candidate,
  });

  return (
    <CardSection name="CANDIDATE_CARD_AVAILABILITY_ROW" divider>
      <Gutter bottom="16px">
        {isEnvoyUser && candidate.isHiddenExternal ? (
          <Gutter bottom="16px">
            <Banner name="HIDDEN_TALENT_BANNER" type="info" impact="high">
              <Gutter bottom="8px">
                <ParagraphHeader>
                  Not currently visible to clients.
                </ParagraphHeader>{' '}
                <Paragraph name="BANNER_BODY_COPY">
                  This talent was hidden by{' '}
                  {candidate.isHiddenExternalUpdatedByUser?.fullName ??
                    'an Axiom user'}
                  {candidate.isHiddenExternalUpdatedAt
                    ? ` on ${DateUtil.displayDate(candidate.isHiddenExternalUpdatedAt)}`
                    : ''}
                </Paragraph>
              </Gutter>
              <Button
                name="SHOW_TALENT"
                pattern="primary"
                variation="outline"
                onClick={() => {
                  onClickShowTalent();
                }}
              >
                Show on Bench
              </Button>
            </Banner>
          </Gutter>
        ) : null}
        <Well background="gray">
          <MyBenchCardAvailability candidate={candidate} />
          <MyBenchCardExpectedChange candidate={candidate} />
          <Gutter bottom="8px" />
          {isEngaged ? (
            <Button
              disabled={isEnvoyUser}
              name="MANAGE_ENGAGEMENT"
              pattern="secondary"
              onClick={() => {
                onClickEditEngage('manage');
              }}
            >
              Manage Engagement
            </Button>
          ) : (
            <Button
              disabled={isEnvoyUser}
              name="RE_ENGAGE"
              pattern="secondary"
              onClick={() => {
                onClickEditEngage('re-engage');
              }}
            >
              Re-engage
            </Button>
          )}
        </Well>
      </Gutter>
      <Gutter bottom="16px">
        {isEngaged ? (
          <Layout>
            <LayoutItem>
              <CondensedLarge>Currently engaged</CondensedLarge>
              <CondensedLarge name="ROLLING_OFF_DATE">
                Rolling off on{' '}
                <CondensedHeader as="span">
                  {DateUtil.displayDateFullYearShortMonth(
                    candidate.soonestEngagementEndDate
                  )}
                </CondensedHeader>
              </CondensedLarge>
              <CondensedLarge name="ENGAGED_FOR">
                Engaged for{' '}
                <CondensedHeader as="span">
                  {candidate.billingUnitsPerWeek}{' '}
                  {PluralizeUtil({
                    text: 'hour',
                    count: candidate.billingUnitsPerWeek ?? 0,
                  })}
                </CondensedHeader>{' '}
                per week
              </CondensedLarge>
            </LayoutItem>
            {candidate.hasOtherEngagements ? (
              <LayoutItem fluid position="right">
                <Tooltip
                  name="ROLLING_OFF_DATE_TOOLTIP"
                  position="top"
                  anchor={
                    <IconButton
                      pattern="primary"
                      variation="minimal"
                      icon="circle-info"
                      name="TOOLTIP_ANCHOR"
                    />
                  }
                >
                  These details are for the talent's nearest roll-off date. For
                  their other engagements, please contact your Axiom team.
                </Tooltip>
              </LayoutItem>
            ) : null}
          </Layout>
        ) : (
          <CondensedLarge name="PREVIOUSLY_ENGAGED">
            Previously engaged
          </CondensedLarge>
        )}
      </Gutter>
      <Gutter bottom="16px">
        <Well name="CANDIDATE_CARD_CANDIDATE_NOTES_ROW" background="light blue">
          {candidate.calculatedNotes ? (
            <ReadonlyTextarea
              name="NOTES"
              formatting="modern"
              value={candidate.calculatedNotes}
            />
          ) : (
            <Flashy name="NO_NOTES" italic>
              Profile summary not available. To see their full bio click View
              Profile below.
            </Flashy>
          )}
        </Well>
      </Gutter>
      <Gutter bottom="16px">
        <CondensedHeader>Industries</CondensedHeader>
        <CondensedLarge name="INDUSTRIES">
          {displayableIndustries.length > 0 ? (
            <Divider>
              {displayableIndustries.map(i => {
                // To appease the TS Gods
                const { displayValue } =
                  taxonomy.industries[i.industryValue] ?? {};
                return displayValue ? (
                  <span data-test="INDUSTRY" key={i.industryValue}>
                    {displayValue}
                  </span>
                ) : null;
              })}
            </Divider>
          ) : (
            'Not Available'
          )}
        </CondensedLarge>
      </Gutter>
      <Gutter bottom="16px">
        <CondensedHeader>Practice Area</CondensedHeader>
        <CondensedLarge name="PA_FA">{practiceAreaText}</CondensedLarge>
      </Gutter>
      {formattedLegalRole ? (
        <Gutter name="LEGAL_ROLE_SECTION" bottom="16px">
          <CondensedHeader>Legal Role</CondensedHeader>
          <CondensedLarge name="LEGAL_ROLE">
            {formattedLegalRole}
          </CondensedLarge>
        </Gutter>
      ) : null}
      <Gutter bottom="16px">
        <CondensedHeader>Years of Experience</CondensedHeader>
        <CondensedLarge name="YEARS_OF_EXPERIENCE">
          {typeof candidate.yearsOfExperience === 'number'
            ? `${TalentUtil.formatYearsOfExperience(candidate.yearsOfExperience)} years`
            : 'Not Available'}
        </CondensedLarge>
      </Gutter>
      <Gutter bottom="16px">
        <CondensedHeader>Law Schools</CondensedHeader>
        <CondensedLarge name="LAW_SCHOOLS">
          {uniqueLawDegreeSchools.length > 0 ? (
            <Divider>
              {uniqueLawDegreeSchools.map(lawDegreeSchool => (
                <span data-test="DEGREE_SCHOOL" key={lawDegreeSchool}>
                  {lawDegreeSchool}
                </span>
              ))}
            </Divider>
          ) : (
            'Not Available'
          )}
        </CondensedLarge>
      </Gutter>
      <Gutter bottom="0px">
        <CondensedHeader>Location</CondensedHeader>
        <CondensedLarge name="LOCATION">
          {[candidate.addressState, candidate.addressCountry]
            .filter(Boolean)
            .join(', ') || 'Not Available'}
        </CondensedLarge>
      </Gutter>
    </CardSection>
  );
};
